import type { FilterId } from '../../constants/filters';
import { FILTER_EXCLUSION_RULES } from '../../constants/filters/filter-exclusion-rules';
import type { FilterExclusionRule } from '../../constants/filters/filter-exclusion-rules';
import { FILTER_INCLUSION_RULES } from '../../constants/filters/filter-inclusion-rules';
import type { FilterInclusionRule } from '../../constants/filters/filter-inclusion-rules';
import type { Filters } from '../../constants/filters/types';

/**
 * Extracts the selected values for a given filter from the store's filters map.
 * Select filters store values as `value: string[]`; boolean and other filter
 * types are not expected to appear in rules and return an empty array.
 */
const getFilterValues = (filters: Filters, filterId: string): string[] => {
	const filter = filters[filterId];
	if (filter && 'value' in filter && Array.isArray(filter.value)) {
		return filter.value;
	}
	return [];
};

/**
 * Returns the set of FilterIds that should be hidden based on the currently
 * selected product and the current filter store state.
 *
 * Two rule types are evaluated:
 *
 * **Exclusion rules** (`FILTER_EXCLUSION_RULES`):
 * - Hide filters when conditions ARE met (e.g. hide lastUpdated when Slack + Conversation).
 * - Filters with active selections are never hidden by exclusion rules — the user's
 *   prior interaction is preserved.
 *
 * **Inclusion rules** (`FILTER_INCLUSION_RULES`):
 * - Filters referenced by inclusion rules are hidden by default and only shown when
 *   at least one matching rule has ALL of its conditions met.
 * - If no condition filter has a selection yet (e.g. "All" mode), the managed filter
 *   follows its normal visibility and is NOT hidden prematurely.
 * - Unlike exclusion rules, inclusion rules DO hide filters even when they have active
 *   selections. Stale selections are kept out of URL/GraphQL/UI/badge consumers, and
 *   `setSearchStoreState` clears the underlying store state for excluded filters so
 *   the store stays in sync with what is visible/applied.
 *
 * @param selectedProduct - The currently selected product key, or undefined if "All"
 * @param filters - The store's filters map (Record of filterId → filter state)
 * @param exclusionRules - Override for testing (defaults to FILTER_EXCLUSION_RULES)
 * @param inclusionRules - Override for testing (defaults to FILTER_INCLUSION_RULES)
 */
export const getExcludedFilterIds = (
	selectedProduct: string | undefined,
	filters: Filters,
	exclusionRules: FilterExclusionRule[] = FILTER_EXCLUSION_RULES,
	inclusionRules: FilterInclusionRule[] = FILTER_INCLUSION_RULES,
): Set<FilterId> => {
	const excluded = new Set<FilterId>();

	// ── Exclusion rules: hide filters when conditions ARE met ──
	for (const rule of exclusionRules) {
		// Check product condition (if specified)
		if (rule.selectedProduct !== undefined && rule.selectedProduct !== selectedProduct) {
			continue;
		}

		// Check all filter selection conditions (AND logic — all must match)
		const allFiltersMatch = rule.selectedFilters.every(([filterId, requiredValue]) => {
			const currentValues = getFilterValues(filters, filterId);
			return currentValues.includes(requiredValue);
		});

		if (allFiltersMatch) {
			rule.hiddenFilters.forEach((f) => {
				// Never hide a filter that currently has active selections — the user
				// may have chosen a value before the exclusion rule activated and the
				// selection is still valid.
				const filterValues = getFilterValues(filters, f);
				if (filterValues.length === 0) {
					excluded.add(f);
				}
			});
		}
	}

	// ── Inclusion rules: hide filters when conditions are NOT met ──
	// Step 1: Collect all filter IDs managed by inclusion rules and track which
	// condition filter IDs each managed filter depends on.
	const managedFilters = new Set<FilterId>();
	const satisfiedFilters = new Set<FilterId>();
	const conditionFiltersPerManaged = new Map<FilterId, Set<string>>();

	for (const rule of inclusionRules) {
		// Check product condition first — skip rules for other products entirely
		if (rule.selectedProduct !== undefined && rule.selectedProduct !== selectedProduct) {
			continue;
		}

		rule.shownFilters.forEach((f) => {
			managedFilters.add(f);
			if (!conditionFiltersPerManaged.has(f)) {
				conditionFiltersPerManaged.set(f, new Set());
			}
			rule.selectedFilters.forEach(([filterId]) =>
				conditionFiltersPerManaged.get(f)!.add(filterId),
			);
		});

		// Check if this rule's conditions are all met
		const allConditionsMet = rule.selectedFilters.every(([filterId, requiredValue]) => {
			const currentValues = getFilterValues(filters, filterId);
			return currentValues.includes(requiredValue);
		});

		if (allConditionsMet) {
			rule.shownFilters.forEach((f) => satisfiedFilters.add(f));
		}
	}

	// Step 2: For each inclusion-managed filter, hide it if:
	// - No inclusion rule matched (filter not satisfied), AND
	// - At least one condition filter has an active selection (the user has made
	//   a choice that makes the filter irrelevant). If no condition filter has a
	//   value yet (e.g. "All" mode), leave the filter visible — don't hide it
	//   prematurely before the user has picked a type.
	for (const filterId of managedFilters) {
		if (!satisfiedFilters.has(filterId)) {
			const conditionFilterIds = conditionFiltersPerManaged.get(filterId) ?? new Set();
			const anyConditionFilterHasValue = [...conditionFilterIds].some(
				(cfId) => getFilterValues(filters, cfId).length > 0,
			);

			if (anyConditionFilterHasValue) {
				// A condition filter has a selection but it doesn't match any rule —
				// hide the managed filter. Unlike exclusion rules, inclusion rules hide
				// even when the managed filter has active selections — the store-level
				// `setSearchStoreState` will clear those selections separately so the
				// store stays in sync with what is applied.
				excluded.add(filterId);
			}
		}
	}

	return excluded;
};
